import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CustomerCard, useCustomerForm } from '#/features/order-create'
import type {
  CustomerSavedField,
  EditCustomerDefaults,
  EditCustomerPayload,
} from '#/features/order-create'
import type { ShipmentDetail } from '#/shared/api/generated/shipment/models'
import { CallableEmail } from '#/shared/ui/email-address'
import { EmailIcon } from '#/shared/ui/icons'
import { CallablePhone } from '#/shared/ui/phone-number'
import { EditFooter, SectionEditButton } from '#/widgets/shipment-card'

import { useCustomerEdit } from '../model/use-shipment-section-edit'
import { SidebarSection } from './sidebar-section'

type Customer = ShipmentDetail['customer']

interface ContactLine {
  kind: 'phone' | 'email'
  value: string
  caption?: string
}

function pick(value: string | null | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined
}

function phoneLine(
  value: string | null | undefined,
  caption?: string,
): ContactLine | undefined {
  const v = pick(value)
  return v ? { kind: 'phone', value: v, caption } : undefined
}

function emailLine(value: string | null | undefined): ContactLine | undefined {
  const v = pick(value)
  return v ? { kind: 'email', value: v } : undefined
}

function compact(lines: (ContactLine | undefined)[]): ContactLine[] {
  return lines.filter((line): line is ContactLine => Boolean(line))
}

function ContactRow({
  line,
  name,
  shipmentId,
  customerId,
}: {
  line: ContactLine
  name: string
  shipmentId?: string
  customerId?: string
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {/* Phone rows get their call icon from CallablePhone; only emails need a
          leading icon here. */}
      {line.kind === 'email' && (
        <EmailIcon className="size-3.5 shrink-0 text-gray-500" />
      )}
      {line.kind === 'phone' ? (
        <CallablePhone
          value={line.value}
          className="text-sm text-gray-900"
          smsTarget={{ phone: line.value, name, shipmentId, customerId }}
          hideIcon
        />
      ) : (
        <CallableEmail
          value={line.value}
          className="text-sm text-gray-900"
          target={{ email: line.value, name, shipmentId, customerId }}
          hideIcon
        />
      )}
      {line.caption && (
        <>
          <span className="size-1 shrink-0 rounded-full bg-gray-300" />
          <span className="shrink-0 text-xs text-gray-400">{line.caption}</span>
        </>
      )}
    </div>
  )
}

function ContactColumn({
  name,
  lines,
  shipmentId,
  customerId,
}: {
  name: string
  lines: ContactLine[]
  shipmentId?: string
  customerId?: string
}) {
  if (!name && lines.length === 0) return null
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.75">
      {name && (
        <span className="truncate text-sm font-medium text-gray-900">
          {name}
        </span>
      )}
      {lines.map((line, index) => (
        <ContactRow
          key={index}
          line={line}
          name={name}
          shipmentId={shipmentId}
          customerId={customerId}
        />
      ))}
    </div>
  )
}

interface CustomerColumns {
  contactName: string
  contactLines: ContactLine[]
  company: string
  companyLines: ContactLine[]
}

const EMPTY_COLUMNS: CustomerColumns = {
  contactName: '',
  contactLines: [],
  company: '',
  companyLines: [],
}

function useCustomerColumns(customer: Customer | undefined): CustomerColumns {
  const { t } = useTranslation()
  if (!customer) return EMPTY_COLUMNS
  return {
    contactName: [customer.first_name, customer.last_name]
      .filter(Boolean)
      .join(' ')
      .trim(),
    contactLines: compact([
      phoneLine(customer.phone, t('orderDetails.customer.phoneMain')),
      phoneLine(customer.secondary_phone, t('orderDetails.customer.phoneAlt')),
      emailLine(customer.email),
      emailLine(customer.secondary_email),
    ]),
    company: pick(customer.company) ?? '',
    companyLines: compact([
      emailLine(customer.billing_email),
      phoneLine(customer.company_phone),
    ]),
  }
}

function CustomerView({
  shipment,
  onEdit,
}: {
  shipment: ShipmentDetail
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const { contactName, contactLines, company, companyLines } =
    useCustomerColumns(shipment.customer)
  const shipmentId = shipment.id
  const customerId = shipment.customer?.id ?? undefined
  return (
    <SidebarSection label={t('orderDetails.customer.title')} inset>
      <div className="group/section relative -mx-2.5 flex items-start gap-5 rounded-md px-2.5 py-1 hover:bg-gray-50">
        <SectionEditButton
          label={t('orderDetails.customer.title')}
          onClick={onEdit}
        />
        <ContactColumn
          name={contactName}
          lines={contactLines}
          shipmentId={shipmentId}
          customerId={customerId}
        />
        <ContactColumn
          name={company}
          lines={companyLines}
          shipmentId={shipmentId}
          customerId={customerId}
        />
      </div>
    </SidebarSection>
  )
}

function CustomerEdit({
  defaultValues,
  isSaving,
  onCancel,
  onSubmit,
}: {
  defaultValues: EditCustomerDefaults
  isSaving: boolean
  onCancel: () => void
  onSubmit: (data: EditCustomerPayload) => Promise<void>
}) {
  const { t } = useTranslation()
  const customer = useCustomerForm({
    defaultValues: defaultValues.customer,
    onSubmit: (data) => onSubmit({ customer: data }),
  })
  const [savedField, setSavedField] = useState<CustomerSavedField | null>(null)
  const submitting = isSaving || customer.isSubmitting

  return (
    <div className="flex flex-col gap-1 rounded-lg bg-gray-100">
      <div className="flex h-6 items-center px-2.5">
        <span className="text-xs uppercase text-gray-500">
          {t('orderDetails.customer.title')}
        </span>
      </div>
      <CustomerCard
        bare
        searchPlaceholders
        control={customer.control}
        register={customer.register}
        setValue={customer.setValue}
        errors={customer.errors}
        onCustomerSelect={() => setSavedField(null)}
        customerSavedField={savedField}
        onSaveAsNewCustomer={setSavedField}
        onClearNewCustomer={() => setSavedField(null)}
      />
      <EditFooter
        onCancel={onCancel}
        onSave={() => void customer.handleSubmit?.()}
        disabled={submitting}
      />
    </div>
  )
}

export function SidebarCustomerSection({
  shipment,
}: {
  shipment: ShipmentDetail
}) {
  const [editing, setEditing] = useState(false)
  const { defaults, isSaving, onSubmit } = useCustomerEdit(shipment, () =>
    setEditing(false),
  )

  if (editing) {
    return (
      <CustomerEdit
        defaultValues={defaults}
        isSaving={isSaving}
        onCancel={() => setEditing(false)}
        onSubmit={onSubmit}
      />
    )
  }

  return <CustomerView shipment={shipment} onEdit={() => setEditing(true)} />
}
